// Story 1.21 — dynamic linked-accounts row editor.
//
// 1-5 rows; each row has a kind <select> + url <input> + a Remove
// button (disabled on the last remaining row so the form always has
// at least one). Per-row error highlighting from the server's 422
// failure detail.
//
// Platform kinds show their base URL as a fixed prefix OUTSIDE the
// input (users kept failing validation by typing "twitch.tv/name"
// without the scheme) — the user types only their handle/path, and the
// row's stored url is always the full https URL. "website" stays a
// free-form https input. An empty handle stores url="" (never the bare
// prefix), so "missing" detection downstream keeps working.

import { Button, Input, Label } from "@greenhold/components";

import type { LinkedAccountInput } from "./useSubmitCreatorApplication";

const KINDS = ["twitch", "youtube", "kick", "twitter", "website"] as const;

/** Fixed URL prefix per platform kind; absent = free-form https URL. */
export const KIND_URL_PREFIX: Record<string, string> = {
  twitch: "https://twitch.tv/",
  youtube: "https://youtube.com/",
  kick: "https://kick.com/",
  twitter: "https://x.com/",
};

/** The user-typed part after the platform prefix. Tolerates full URLs
 *  pasted into the handle box (strips scheme/www/known domains). */
function remainderOf(kind: string, url: string): string {
  const prefix = KIND_URL_PREFIX[kind];
  if (!prefix) return url;
  if (url.startsWith(prefix)) return url.slice(prefix.length);
  return url
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/^(twitch\.tv|youtube\.com|kick\.com|x\.com|twitter\.com)\//i, "");
}

const MAX_ROWS = 5;

interface Props {
  rows: LinkedAccountInput[];
  errors: Record<number, string>;
  onChange: (rows: LinkedAccountInput[]) => void;
}

export function LinkedAccountsField({ rows, errors, onChange }: Props) {
  const updateRow = (index: number, patch: Partial<LinkedAccountInput>) => {
    const next = rows.slice();
    next[index] = { ...next[index], ...patch };
    onChange(next);
  };
  const setKind = (index: number, kind: string) => {
    // Carry the handle across platform switches (twitch→kick keeps
    // "adam"); switching to website keeps the full URL as-is.
    const row = rows[index];
    const prefix = KIND_URL_PREFIX[kind];
    if (!prefix) {
      updateRow(index, { kind });
      return;
    }
    const rem = remainderOf(row.kind, row.url);
    updateRow(index, { kind, url: rem ? prefix + rem : "" });
  };
  const setRemainder = (index: number, kind: string, typed: string) => {
    const prefix = KIND_URL_PREFIX[kind];
    if (!prefix) {
      updateRow(index, { url: typed });
      return;
    }
    const rem = remainderOf(kind, typed.trim());
    updateRow(index, { url: rem ? prefix + rem : "" });
  };
  const addRow = () => {
    if (rows.length >= MAX_ROWS) return;
    onChange([...rows, { kind: "twitch", url: "" }]);
  };
  const removeRow = (index: number) => {
    if (rows.length <= 1) return;
    onChange(rows.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      <Label>Linked accounts *</Label>
      <p className="text-sm opacity-70">
        At least one URL we can verify. Twitch, YouTube, Kick, X/Twitter, or
        your personal site.
      </p>
      {rows.map((row, index) => {
        const error = errors[index];
        const prefix = KIND_URL_PREFIX[row.kind];
        return (
          <div key={index} className="space-y-1">
            <div className="flex gap-2 items-start">
              <select
                value={row.kind}
                onChange={(e) => setKind(index, e.target.value)}
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                aria-label={`Kind for link ${index + 1}`}
              >
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
              {prefix ? (
                <div className="flex flex-1 items-stretch">
                  <span
                    aria-hidden="true"
                    className="flex items-center rounded-l-md border border-r-0 border-input bg-brand-paper-200 px-3 font-mono text-sm opacity-80 whitespace-nowrap"
                  >
                    {prefix}
                  </span>
                  <Input
                    type="text"
                    value={remainderOf(row.kind, row.url)}
                    onChange={(e) => setRemainder(index, row.kind, e.target.value)}
                    placeholder="yourhandle"
                    className="flex-1 rounded-l-none"
                    aria-label={`${row.kind} handle for link ${index + 1} (${prefix} is prepended automatically)`}
                    aria-invalid={!!error}
                    aria-describedby={error ? `link-err-${index}` : undefined}
                  />
                </div>
              ) : (
                <Input
                  type="url"
                  value={row.url}
                  onChange={(e) => setRemainder(index, row.kind, e.target.value)}
                  placeholder="https://..."
                  className="flex-1"
                  aria-invalid={!!error}
                  aria-describedby={error ? `link-err-${index}` : undefined}
                />
              )}
              <Button
                type="button"
                variant="tertiary"
                onClick={() => removeRow(index)}
                disabled={rows.length <= 1}
              >
                Remove
              </Button>
            </div>
            {error && (
              <p
                id={`link-err-${index}`}
                className="text-sm text-destructive pl-1"
              >
                {error}
              </p>
            )}
          </div>
        );
      })}
      {rows.length < MAX_ROWS && (
        <Button type="button" variant="tertiary" onClick={addRow}>
          Add another link
        </Button>
      )}
    </div>
  );
}
